import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import DataTable from 'prefabsComponents/Tables/DataTable';
import DashboardLayout from 'prefabsComponents/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'prefabsComponents/Navbars/DashboardNavbar';
import Footer from 'prefabsComponents/Footer';
import OrderCard from 'components/OrderCard';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import AccionesCell from 'layouts/pages/orders/order-shipment/components/AccionesCell';
import ShipmentModal from './components/ShipmentModal';
import StatusCell from 'layouts/pages/orders/order-shipment/components/StatusCell';

import Swal from 'sweetalert2';
import { AuthContext } from 'context/AuthContext';

function OrderShipmentList() {
  const { numeroOrdenCabecera } = useParams();
  const navigate = useNavigate();

  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] });
  const [orderInfo, setOrderInfo] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentShipment, setCurrentShipment] = useState(null);

  // 🔒 Roles
  const { hasRole } = useContext(AuthContext);
  const canAddShipments = hasRole('admin', 'user'); // los clientes no pueden agregar
  const isClient = hasRole('client');                // cliente: ocultar precio y subtotal

  // Función reutilizable para obtener y actualizar la lista de embarques
  const fetchShipments = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments/${numeroOrdenCabecera}/`
      );
      const embarques = response.data;

      const columns = [
        {
          Header: '',
          accessor: 'status',
          Cell: ({ row }) => {
            const statusValue = row.original.status;
            const icon = statusValue === 0 || statusValue === '0' ? 'drafts' : 'email';
            const color = statusValue === 0 || statusValue === '0' ? 'warning' : 'success';
            return <StatusCell icon={icon} color={color} status={String(statusValue)} />;
          },
        },
        { Header: 'Descripción', accessor: 'producto' },
        { Header: 'Origen', accessor: 'origen' },
        { Header: 'Lugar Entrega', accessor: 'lugarentrega' },
        { Header: 'Incoterm', accessor: 'incoterm' },
        { Header: 'Entrega', accessor: 'fechaentrega' },
        { Header: 'Cantidad', accessor: 'cantidad' },

        // ⬇️ Mostrar Precio y Sub Total solo si NO es cliente
        ...(!isClient
          ? [
              { Header: 'Precio', accessor: 'precio' },
              { Header: 'Sub Total', accessor: 'sub_total' },
            ]
          : []),

        {
          Header: 'Acciones',
          accessor: 'acciones',
          Cell: ({ row }) => (
            <AccionesCell
              id_orden_detalle={row.original.id_orden_detalle}
              numeroOrdenCabecera={numeroOrdenCabecera}
              status={row.original.status}
              onEdit={() => handleEdit(row.original)}
              onDelete={() => handleDelete(row.original.id_orden_detalle)}
              onFinalize={handleFinalize}
              onAperturar={handleOpenShipment}
            />
          ),
        },
      ];

      const rows = embarques.map((embarque) => ({
        ...embarque,
        sub_total:
          parseFloat(String(embarque.cantidad).replace(/,/g, '')) *
          parseFloat(String(embarque.precio).replace(/,/g, '')),
      }));

      setDataTableData({ columns, rows });
    } catch (error) {
      console.error('Error fetching shipment data:', error);
    }
  }, [numeroOrdenCabecera, isClient]);

  // Cargar embarques al montar o al cambiar orden o rol
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  // Obtener información de la orden (cabecera)
  useEffect(() => {
    const fetchOrderInfo = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/orden-info/?id=${numeroOrdenCabecera}`
        );
        const orders = response.data;
        if (orders && orders.length > 0) {
          setOrderInfo(orders[0]);
        }
      } catch (error) {
        console.error('Error fetching order info:', error);
      }
    };

    fetchOrderInfo();
  }, [numeroOrdenCabecera]);

  const handleOpenAdd = () => {
    setCurrentShipment(null);
    setOpenModal(true);
  };

  const handleEdit = async (shipment) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-info/`,
        { params: { id_orden_detalle: shipment.id_orden_detalle } }
      );
      const shipmentData = Array.isArray(response.data) ? response.data[0] : response.data;
      setCurrentShipment(shipmentData);
      setOpenModal(true);
    } catch (error) {
      console.error('Error fetching shipment detail:', error);
      Swal.fire('Error', 'No se pudo obtener los detalles del embarque.', 'error');
    }
  };

  const handleDelete = async (id_orden_detalle) => {
    const result = await Swal.fire({
      title: '¿Eliminar el embarque?',
      text: 'Esta acción no se puede deshacer. ¿Estás seguro?',
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/orders/shipments/${id_orden_detalle}/`, {
          data: { force: false },
        });
        Swal.fire('Embarque eliminado', 'El embarque ha sido eliminado con éxito.', 'success');
        fetchShipments();
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || 'Hubo un problema al eliminar el embarque.';
        Swal.fire('Error', errorMessage, 'error');
      }
    }
  };

  const handleFinalize = async (id_orden_detalle) => {
    const result = await Swal.fire({
      title: '¿Finalizar el embarque?',
      text: 'Esta acción marcará el embarque como finalizado. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, finalizar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-finalize/${id_orden_detalle}/`
        );
        Swal.fire('¡Éxito!', response.data.message, 'success');
        fetchShipments();
      } catch (error) {
        Swal.fire('Error', 'El embarque no fue finalizado, inténtelo nuevamente!', 'error');
        console.error('Error finalizando el embarque:', error);
      }
    }
  };

  const handleOpenShipment = async (id_orden_detalle) => {
    const result = await Swal.fire({
      title: 'Aperturar el embarque?',
      text: 'Esta acción devolverá el embarque a aperturado. ¿Estás seguro?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, aperturar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-open/${id_orden_detalle}/`
        );
        Swal.fire('¡Éxito!', response.data.message, 'success');
        fetchShipments();
      } catch (error) {
        Swal.fire('Error', 'El embarque no fue aperturado, inténtelo nuevamente!', 'error');
        console.error('Error aperturando el embarque:', error);
      }
    }
  };

  const handleSaveShipment = async (formData) => {
    try {
      const transformDate = (date) => {
        if (date && !(date instanceof Date)) {
          const parsed = new Date(date);
          if (!isNaN(parsed)) return parsed.toISOString();
        } else if (date instanceof Date) {
          return date.toISOString();
        }
        return '';
      };

      const extractId = (field) =>
        typeof field === 'object' && field !== null ? field.value : field;

      const transformedShipment = {
        descripcion_documento: formData.descripcion,
        fechaentrega_orden_detalle: transformDate(formData.fecha_entrega),
        id_tipo_embalaje: extractId(formData.tipo_embalaje),
        comprador: formData.comprador,
        id_tipo_despacho: formData.tipo_despacho,
        pais_origen_embarque: formData.pais_origen,
        id_intercom: extractId(formData.incoterms),
        lugar_entrega: formData.lugar_entrega,
        cantidad_unidad: formData.cantidad_unidades,
        termino_pago: formData.termino_pago,
        id_tipo_moneda: extractId(formData.tipo_moneda),
        precio_unidad: formData.precio,
        fecharealentrega_orden_detalle: transformDate(formData.fecharealentrega_orden_detalle),
        fechaembarque_orden_detalle: transformDate(formData.fechaembarque_orden_detalle),
        fechaarribo_orden_detalle: transformDate(formData.fechaarribo_orden_detalle),
      };

      if (currentShipment) {
        await axios.patch(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments/${currentShipment.id}/`,
          transformedShipment
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Embarque actualizado exitosamente',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        const payload = { embarques: [transformedShipment] };
        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments/${numeroOrdenCabecera}/`,
          payload
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Nuevo embarque agregado',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }

      setCurrentShipment(null);
      setOpenModal(false);
      fetchShipments();
    } catch (error) {
      console.error('Error al guardar el embarque:', error);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Error al guardar el embarque',
        text: error.response?.data?.error || error.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const orderCardInfo = orderInfo
    ? {
        cliente: orderInfo.cliente,
        'Fecha de Embarque': orderInfo.fecha,
        'Fecha de Arribo': orderInfo.entrega,
        'Fecha Real de Arribo': orderInfo.real_entrega,
        responsable: orderInfo.responsable,
        proveedor: orderInfo.proveedor,
        incoterms: orderInfo.incoterms,
        transporte: orderInfo.transporte,
        origen: orderInfo.origen,
        status: orderInfo.status,
      }
    : {};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mb={2} display="flex" flexDirection="column" alignItems="flex-end">
        <IconButton
          aria-label="back"
          onClick={() => navigate(-1)}
          style={{ padding: '4px', fontSize: '1.275rem' }}
        >
          <ArrowBackIosNewIcon fontSize="small" style={{ marginRight: '4px' }} />
          Regresar
        </IconButton>
      </SoftBox>
      <SoftBox my={3}>
        <SoftBox display="flex" flexDirection="row" gap={2} alignItems="flex-start">
          <OrderCard numero={orderInfo ? `#${orderInfo.numero}` : ''} info={orderCardInfo} />
        </SoftBox>

        <SoftBox mt={4}>
          {/* 👇 Botón oculto para clientes */}
          {canAddShipments && (
            <SoftButton
              variant="gradient"
              color="info"
              style={{ marginBottom: '8px' }}
              onClick={handleOpenAdd}
            >
              Agregar Embarques
            </SoftButton>
          )}

          <Card>
            <DataTable
              table={dataTableData}
              entriesPerPage={false}
              canSearch
              title={`Documentos de Orden #${numeroOrdenCabecera}`}
            />
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
      <ShipmentModal
        show={openModal}
        handleClose={() => {
          setCurrentShipment(null);
          setOpenModal(false);
        }}
        handleSave={handleSaveShipment}
        initialData={currentShipment}
      />
    </DashboardLayout>
  );
}

OrderShipmentList.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id_orden_detalle: PropTypes.number.isRequired,
      status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }).isRequired,
  }),
};

export default OrderShipmentList;
